import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import {
  setCurrentTenant,
  useCurrentTenant,
  useCurrentUser,
} from '_graphql/cache/auth';
import { getTenantIdentification } from 'utils/subdomain';
import {
  GET_ORGANIZATION_BY_CUSTOM_DOMAIN,
  Organization,
  GET_ORGANIZATION_BY_SLUG_MIN,
} from '_graphql/operations/organization';
import { Loader } from './layouts';

interface SubdomainResolverProps {
  children: ReactNode;
}

/**
 * SubdomainResolver Component
 *
 * Resolves the tenant organization based on the URL pattern.
 * This component should be placed high in the component tree to ensure
 * tenant context is available before protected routes render.
 *
 * Supported URL Patterns:
 * 1. App Domain Subdomain: acme.tendo.one/supplier
 *    - Resolves tenant by slug (subdomain)
 *
 * 2. Custom Domain: supplier.acme-corp.com
 *    - Resolves tenant by custom domain
 *
 * Flow:
 * 1. Extract tenant identification from URL
 * 2. Query organization by slug OR custom domain
 * 3. Set active organization in cache
 * 4. Show 404 if org not found
 *
 * Local Development:
 * - Use query param: http://localhost:5173?tenant=acme
 * - Or modify /etc/hosts: 127.0.0.1 acme.tendo.local
 */
export const SubdomainResolver: FC<SubdomainResolverProps> = ({ children }) => {
  const currentTenant = useCurrentTenant();
  const currentUser = useCurrentUser();
  const [resolving, setResolving] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [getOrgBySlug, { loading: getOrgBySlugLoading }] = useLazyQuery(
    GET_ORGANIZATION_BY_SLUG_MIN
  );

  const [getOrgByCustomDomain, { loading: getOrgByCustomDomainLoading }] =
    useLazyQuery(GET_ORGANIZATION_BY_CUSTOM_DOMAIN);

  const resolvedTenantRef = useRef<string | null>(null);
  const isResolvingRef = useRef(false);

  useEffect(() => {
    const resolveSubdomain = async () => {
      const tenantId = getTenantIdentification();

      // No tenant identification found - this is OK, might be a public page
      if (!tenantId.slug && !tenantId.customDomain) {
        setResolving(false);
        return;
      }

      // Not logged in yet - wait for auth
      if (!currentUser) {
        setResolving(false);
        return;
      }

      // Build a unique key for this tenant resolution
      const tenantKey = tenantId.customDomain || tenantId.slug;

      // Already resolved this tenant or currently resolving
      if (resolvedTenantRef.current === tenantKey || isResolvingRef.current) {
        setResolving(false);
        return;
      }

      // Already have the correct tenant loaded
      if (currentTenant) {
        if (tenantId.type === 'custom-domain') {
          // For custom domains, we'd need to check if currentTenant.custom_domain matches
          // For now, just continue if we have a tenant
          // TODO: Add custom_domain to Organization type if needed
        } else if (currentTenant.slug === tenantId.slug) {
          resolvedTenantRef.current = tenantKey;
          setResolving(false);
          return;
        }
      }

      isResolvingRef.current = true;

      try {
        let org: Organization | null = null;

        if (tenantId.type === 'custom-domain' && tenantId.customDomain) {
          // Resolve by custom domain
          const { data } = await getOrgByCustomDomain({
            variables: { customDomain: tenantId.customDomain },
          });
          org = data?.organizationByCustomDomain;
        } else if (tenantId.slug) {
          // Resolve by slug
          const { data } = await getOrgBySlug({
            variables: { slug: tenantId.slug },
          });
          org = data?.organizationBySlugMinimal;
        }

        if (org) {
          setCurrentTenant(org);
          resolvedTenantRef.current = tenantKey;
          setResolving(false);
        } else {
          console.warn(
            `Organization not found for ${tenantId.type}: ${tenantKey}`
          );
          setNotFound(true);
          setResolving(false);
        }
      } catch (err) {
        console.error('Error resolving tenant:', err);
        setNotFound(true);
        setResolving(false);
      } finally {
        isResolvingRef.current = false;
      }
    };

    resolveSubdomain();
  }, [currentTenant, currentUser, getOrgBySlug, getOrgByCustomDomain]);

  const isLoading = useMemo(() => {
    return resolving || getOrgBySlugLoading || getOrgByCustomDomainLoading;
  }, [resolving, getOrgBySlugLoading, getOrgByCustomDomainLoading]);

  if (isLoading) {
    return <Loader />;
  }

  if (notFound) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
          <p className="text-gray-600 mb-4">Organization not found</p>
          <p className="text-sm text-gray-500 mb-6">
            The organization you're trying to access doesn't exist or you don't
            have permission to view it.
          </p>
          <a
            href="/"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
          >
            Go to Home
          </a>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default SubdomainResolver;
